import { Field, FieldProps, useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { BaseFormField } from 'types/form/baseFormField';
import { TagInput, TagInputItem } from '../TagInput';

export interface FormikTagInputProps extends BaseFormField {
  placeholder?: string;
  items: TagInputItem[];
  disabled?: boolean;
  omitKeys?: (string | number)[];
  includeKeys?: (string | number)[];
  selectedItemKeys?: (string | number)[];
  onChange?: (selectedItems: TagInputItem[]) => void;
  maxTags?: number;
}

export const FormikTagInput: React.FC<FormikTagInputProps> = ({
  name,
  items: propsItems,
  label,
  placeholder,
  required,
  disabled,
  selectedItemKeys,
  onChange,
  omitKeys,
  includeKeys,
  maxTags
}) => {
  const { setFieldValue, setFieldTouched, setFieldError, values } = useFormikContext();

  // Mémoïser les items filtrés
  const items = useMemo(() => {
    let filteredItems = propsItems;

    // Si includeKeys est défini, ne garder que ces items
    if (includeKeys && includeKeys.length > 0) {
      filteredItems = filteredItems.filter((item) => includeKeys.includes(item.key as any));
    }

    // Si omitKeys est défini, exclure ces items
    if (omitKeys && omitKeys.length > 0) {
      filteredItems = filteredItems.filter((item) => !omitKeys.includes(item.key as any));
    }

    return filteredItems;
  }, [propsItems, omitKeys, includeKeys]);

  // Récupérer la valeur actuelle du formulaire (array de keys)
  const currentValue = (values as any)[name] || [];

  // Calculer selectedItems de manière dérivée
  const selectedItems = useMemo(() => {
    const keys = onChange ? selectedItemKeys : currentValue;
    if (!Array.isArray(keys)) return [];

    return keys
      .map(key => items.find(item => item.key === key))
      .filter((item): item is TagInputItem => item !== undefined);
  }, [items, currentValue, selectedItemKeys, onChange]);

  // Référence pour éviter les boucles
  const lastUpdatedValueRef = useRef(currentValue);

  // Handler mémoïsé pour la sélection
  const handleSelect = useCallback((newSelectedItems: TagInputItem[]) => {
    const newKeys = newSelectedItems.map(item => item.key);

    if (onChange) {
      // Mode contrôlé
      onChange(newSelectedItems);
    } else {
      // Mode non contrôlé - mettre à jour Formik uniquement si la valeur change
      const hasChanged =
        newKeys.length !== lastUpdatedValueRef.current.length ||
        newKeys.some(key => !lastUpdatedValueRef.current.includes(key));

      if (hasChanged) {
        lastUpdatedValueRef.current = newKeys;
        setFieldTouched(name, true, false);
        setFieldValue(name, newKeys, false);
        setFieldError(name, undefined);
      }
    }
  }, [onChange, name, setFieldValue, setFieldTouched, setFieldError]);

  // Synchroniser la référence quand la valeur externe change
  useEffect(() => {
    lastUpdatedValueRef.current = currentValue;
  }, [currentValue]);

  return (
    <Field name={name}>
      {({ field, meta }: FieldProps) => (
        <TagInput
          {...field}
          selectedItems={selectedItems}
          label={label}
          required={required}
          error={meta.touched && meta.error ? meta.error : ''}
          items={items}
          placeholder={placeholder}
          disabled={disabled}
          onSelect={handleSelect}
          maxTags={maxTags}
        />
      )}
    </Field>
  );
};
